import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import BackgroundVideo from "@/components/BackgroundVideo";
import { GraduationCap, MapPin, Globe, Search, Sparkles } from "lucide-react";

const fmtUsd = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export default function LessonsMarketplace() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState(null);
  const [filterSpec, setFilterSpec] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set("lesson_type", filterType);
      if (filterSpec) params.set("specialty", filterSpec);
      if (search.trim()) params.set("location", search.trim());
      const { data } = await api.get(`/lessons/coaches?${params.toString()}`);
      setData(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterSpec]);

  const catalog = data?.catalog || {};
  const offerings = data?.offerings || [];

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: "var(--ink-1)" }}>
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" className="text-white/70 hover:text-white">Sign in</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[var(--ink-border)]">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <BackgroundVideo tintClass="bg-gradient-to-b from-[rgba(15,30,54,0.85)] to-[rgba(5,11,22,0.95)]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-14">
          <div className="text-[11px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
            ArmIQ Pitching Lessons
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white mt-2">
            Book a session with elite pitching coaches.
          </h1>
          <p className="text-base text-white/70 mt-4 max-w-2xl">
            Browse vetted coaches, pick a lesson format, and pay in seconds. From a 15-minute pitch-design micro-consult to full 60-min masterclasses.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-6 mt-8">
        <div className="rounded-2xl ink-card p-4 grid md:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Search by city</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input placeholder="e.g. Williamsport"
                className="pl-9 bg-[var(--ink-1)] border-[var(--ink-border)] text-white"
                value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                data-testid="lessons-search" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Lesson type</label>
            <select value={filterType || ""} onChange={(e) => setFilterType(e.target.value || null)}
              className="mt-1 h-10 px-3 rounded-md bg-[var(--ink-1)] border border-[var(--ink-border)] text-white text-sm"
              data-testid="lessons-type-filter">
              <option value="">All formats</option>
              {Object.entries(catalog).map(([id, c]) => <option key={id} value={id}>{c.label} · {fmtUsd(c.price_cents)}</option>)}
            </select>
          </div>
          <Button onClick={load} className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="lessons-search-btn">
            Apply
          </Button>
        </div>
      </div>

      {/* Coach grid */}
      <div className="max-w-6xl mx-auto px-6 mt-8">
        {loading ? (
          <div className="text-white/40 py-16 text-center text-sm">Loading marketplace…</div>
        ) : offerings.length === 0 ? (
          <div className="rounded-2xl ink-card p-12 text-center">
            <Sparkles className="h-10 w-10 mx-auto text-white/30 mb-3" />
            <div className="font-display font-bold text-xl text-white">No coaches match those filters yet.</div>
            <div className="text-sm text-white/50 mt-1">Try widening the search or clearing the lesson-type filter.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="lessons-coach-grid">
            {offerings.map((o) => (
              <CoachCard key={o.user_id} offering={o} catalog={catalog} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function CoachCard({ offering, catalog }) {
  const minPrice = Math.min(...(offering.enabled_lesson_types || []).map((t) => catalog[t]?.price_cents || 0).filter(Boolean));
  return (
    <Link to={`/lessons/coach/${offering.user_id}`} className="block rounded-2xl ink-card p-5 hover:border-cyan-400/40 transition border border-transparent"
      data-testid={`lessons-coach-${offering.user_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-cyan-400/15 flex items-center justify-center text-cyan-300 font-bold text-lg flex-shrink-0">
          {offering.headshot_url ? <img src={offering.headshot_url} alt={offering.coach_name} className="w-12 h-12 rounded-full object-cover" /> :
            (offering.coach_name || "C").slice(0, 1)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-white text-base truncate">{offering.coach_name || "Coach"}</div>
          {offering.location_city && (
            <div className="text-xs text-white/50 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {offering.location_city}</div>
          )}
        </div>
      </div>
      {offering.bio && <p className="text-xs text-white/60 mt-3 line-clamp-3">{offering.bio}</p>}
      <div className="mt-3 flex flex-wrap gap-1">
        {(offering.specialties || []).slice(0, 3).map((s) => (
          <span key={s} className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/5 text-white/70 border border-white/10">{s}</span>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between pt-3 border-t border-white/5">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">From</div>
        <div className="font-display font-black text-2xl text-white">{fmtUsd(minPrice)}</div>
      </div>
    </Link>
  );
}
